import React, { useState } from 'react';
import { Currency } from '../types';
import { ExchangeRateWidget } from '../components/ExchangeRateWidget';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Input, Label, Select } from '../components/ui/Forms';
import { Button } from '../components/ui/Button';
import { InfoTooltip } from '../components/ui/InfoTooltip';
import { Check, Users, Flame, Cake, Globe, MapPin, Settings2, TrendingUp, SlidersHorizontal, ArrowLeftRight, AlertTriangle, Trash2 } from 'lucide-react';
import { Settings } from '../types';
import { CURRENCIES, COUNTRIES } from '../utils/countries';
interface SettingsPageProps {
  baseCurrency: Currency;
  onSetBaseCurrency: (currency: Currency) => void;
  partnerNames: [string, string];
  onSaveNames: (names: [string, string]) => void;
  settings: Settings;
  onUpdateSettings: (settings: Settings) => void;
  onResetAll: () => void;
}
export function SettingsPage({
  baseCurrency,
  onSetBaseCurrency,
  partnerNames,
  onSaveNames,
  settings,
  onUpdateSettings,
  onResetAll
}: SettingsPageProps) {
  const [draftNames, setDraftNames] = useState<[string, string]>(partnerNames);
  const [isSaved, setIsSaved] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [activeTab, setActiveTab] = useState<'household' | 'projections' | 'exchange'>('household');
  const handleSave = () => {
    const name1 = draftNames[0].trim() || 'Partner 1';
    const name2 = draftNames[1].trim() || 'Partner 2';
    onSaveNames([name1, name2]);
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
  };
  const updateSetting = <K extends keyof Settings,>(key: K, value: Settings[K]) => {
    onUpdateSettings({
      ...settings,
      [key]: value
    });
  };
  return <div className="flex flex-col md:flex-row gap-4 md:gap-6 min-h-[calc(100vh-8rem)]" data-id="element-1494">
      {/* Horizontal tabs on mobile, vertical sidebar on desktop */}
      <div className="md:w-48 md:shrink-0" data-id="element-1495">
        <div className="flex md:flex-col md:sticky md:top-20 gap-1 overflow-x-auto pb-2 md:pb-0 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide" data-id="element-1496">
          {[{
          key: 'household' as const,
          label: 'Household',
          icon: Users
        }, {
          key: 'projections' as const,
          label: 'Projections',
          icon: SlidersHorizontal
        }, {
          key: 'exchange' as const,
          label: 'Exchange Rates',
          icon: ArrowLeftRight
        }].map(tab => {
          const Icon = tab.icon;
          return <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`flex items-center gap-2 whitespace-nowrap py-2 px-3 text-sm font-medium rounded-lg transition-all text-left ${activeTab === tab.key ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'}`} data-id="element-1497">
                <Icon className="w-4 h-4 shrink-0" data-id="element-1498" />
                {tab.label}
              </button>;
        })}
        </div>
      </div>

      {/* Content Area — full width */}
      <div className="flex-1 min-w-0" data-id="element-1499">
        <div className="animate-in fade-in duration-300 space-y-8" data-id="element-1500">
          {activeTab === 'household' && <>
              <Card data-id="element-1501">
                <CardHeader title="Household Settings" subtitle="Manage partner names and display preferences" data-id="element-1502" />
                <CardContent className="space-y-6" data-id="element-1503">
                  {/* Base Currency */}
                  <div data-id="element-1504">
                    <div className="flex items-center gap-2 mb-4" data-id="element-1505">
                      <div className="bg-blue-100 p-2 rounded-lg" data-id="element-1506">
                        <Globe className="w-5 h-5 text-blue-600" data-id="element-1507" />
                      </div>
                      <div data-id="element-1508">
                        <h4 className="text-base font-semibold text-slate-800" data-id="element-1509">
                          Base Currency
                        </h4>
                        <p className="text-xs text-slate-500" data-id="element-1510">
                          All holdings and projections are converted to this
                          currency
                        </p>
                      </div>
                    </div>
                    <div className="max-w-xs" data-id="element-1511">
                      <Select searchable value={baseCurrency} onChange={e => onSetBaseCurrency(e.target.value as Currency)} options={CURRENCIES.map(c => ({
                    value: c.code,
                    label: `${c.flag} ${c.code} — ${c.name}`
                  }))} data-id="element-1512" />
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-6" data-id="element-1513">
                    <div className="flex items-center gap-2 mb-4" data-id="element-1514">
                      <div className="bg-emerald-100 p-2 rounded-lg" data-id="element-1515">
                        <MapPin className="w-5 h-5 text-emerald-600" data-id="element-1516" />
                      </div>
                      <div data-id="element-1517">
                        <h4 className="text-base font-semibold text-slate-800" data-id="element-1518">
                          Base Country
                        </h4>
                        <p className="text-xs text-slate-500" data-id="element-1519">
                          Default country pre-selected when adding new holdings
                          and properties
                        </p>
                      </div>
                    </div>
                    <div className="max-w-xs" data-id="element-1520">
                      <Select searchable value={settings.baseCountry || 'US'} onChange={e => onUpdateSettings({
                    ...settings,
                    baseCountry: e.target.value
                  })} options={COUNTRIES.map(c => ({
                    value: c.code,
                    label: `${c.flag} ${c.name}`
                  }))} data-id="element-1521" />
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-6" data-id="element-1522">
                    <div className="flex items-center gap-2 mb-4" data-id="element-1523">
                      <div className="bg-indigo-100 p-2 rounded-lg" data-id="element-1524">
                        <Users className="w-5 h-5 text-indigo-600" data-id="element-1525" />
                      </div>
                      <h4 className="text-base font-semibold text-slate-800" data-id="element-1526">
                        Partner Names
                      </h4>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl" data-id="element-1527">
                      <div data-id="element-1528">
                        <Label data-id="element-1529">Partner 1</Label>
                        <Input value={draftNames[0]} onChange={e => setDraftNames([e.target.value, draftNames[1]])} placeholder="e.g. Alice" data-id="element-1530" />
                      </div>
                      <div data-id="element-1531">
                        <Label data-id="element-1532">Partner 2</Label>
                        <Input value={draftNames[1]} onChange={e => setDraftNames([draftNames[0], e.target.value])} placeholder="e.g. Bob" data-id="element-1533" />
                      </div>
                    </div>

                    <div className="mt-4 flex items-center gap-3" data-id="element-1534">
                      <Button onClick={handleSave} data-id="element-1535">Save Names</Button>
                      {isSaved && <span className="text-sm text-emerald-600 flex items-center gap-1 animate-in fade-in" data-id="element-1536">
                          <Check className="w-4 h-4" data-id="element-1537" /> Saved successfully
                        </span>}
                    </div>
                    <p className="text-sm text-slate-500 mt-3" data-id="element-1538">
                      Updating names will automatically update all existing
                      holdings assigned to the previous names. Joint holdings
                      will remain unaffected.
                    </p>
                  </div>

                  <div className="border-t border-slate-100 pt-6" data-id="element-1539">
                    <div className="flex items-center gap-2 mb-4" data-id="element-1540">
                      <div className="bg-rose-100 p-2 rounded-lg" data-id="element-1541">
                        <Cake className="w-5 h-5 text-rose-600" data-id="element-1542" />
                      </div>
                      <div data-id="element-1543">
                        <h4 className="text-base font-semibold text-slate-800" data-id="element-1544">
                          Dates of Birth
                        </h4>
                        <p className="text-xs text-slate-500" data-id="element-1545">
                          Used to show ages at retirement and key milestones
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl" data-id="element-1546">
                      <div data-id="element-1547">
                        <Label data-id="element-1548">{partnerNames[0]}'s Date of Birth</Label>
                        <Input type="date" value={settings.partnerDOBs?.[0] || ''} onChange={e => onUpdateSettings({
                      ...settings,
                      partnerDOBs: [e.target.value, settings.partnerDOBs?.[1] || '']
                    })} max={new Date().toISOString().split('T')[0]} data-id="element-1549" />
                        {settings.partnerDOBs?.[0] && <p className="text-xs text-slate-400 mt-1" data-id="element-1550">
                            Currently{' '}
                            {Math.floor((Date.now() - new Date(settings.partnerDOBs[0]).getTime()) / (365.25 * 24 * 60 * 60 * 1000))}{' '}
                            years old
                          </p>}
                      </div>
                      <div data-id="element-1551">
                        <Label data-id="element-1552">{partnerNames[1]}'s Date of Birth</Label>
                        <Input type="date" value={settings.partnerDOBs?.[1] || ''} onChange={e => onUpdateSettings({
                      ...settings,
                      partnerDOBs: [settings.partnerDOBs?.[0] || '', e.target.value]
                    })} max={new Date().toISOString().split('T')[0]} data-id="element-1553" />
                        {settings.partnerDOBs?.[1] && <p className="text-xs text-slate-400 mt-1" data-id="element-1554">
                            Currently{' '}
                            {Math.floor((Date.now() - new Date(settings.partnerDOBs[1]).getTime()) / (365.25 * 24 * 60 * 60 * 1000))}{' '}
                            years old
                          </p>}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              {/* Danger Zone */}
              <div className="rounded-xl border-2 border-red-200 bg-red-50/30 overflow-hidden" data-id="element-1555">
                <div className="px-6 py-4 border-b border-red-200 bg-red-50/50" data-id="element-1556">
                  <div className="flex items-center gap-2" data-id="element-1557">
                    <AlertTriangle className="w-5 h-5 text-red-600" data-id="element-1558" />
                    <h3 className="text-base font-semibold text-red-900" data-id="element-1559">
                      Danger Zone
                    </h3>
                  </div>
                </div>
                <div className="px-6 py-5" data-id="element-1560">
                  <div className="flex items-start justify-between gap-6" data-id="element-1561">
                    <div data-id="element-1562">
                      <h4 className="text-sm font-semibold text-slate-900" data-id="element-1563">
                        Reset All Application Data
                      </h4>
                      <p className="text-sm text-slate-500 mt-1" data-id="element-1564">
                        Permanently delete all holdings, loans, cash events,
                        snapshots, settings, and partner names. This action
                        cannot be undone.
                      </p>
                    </div>
                    <Button variant="secondary" onClick={() => {
                  setShowResetConfirm(true);
                  setResetConfirmText('');
                }} className="shrink-0 text-red-600 border-red-300 hover:bg-red-100 hover:border-red-400" data-id="element-1565">
                      <Trash2 className="w-4 h-4 mr-2" data-id="element-1566" />
                      Reset Everything
                    </Button>
                  </div>

                  {showResetConfirm && <div className="mt-4 pt-4 border-t border-red-200 max-w-md" data-id="element-1567">
                      <Label className="text-red-800" data-id="element-1568">
                        Type{' '}
                        <span className="font-mono font-bold bg-red-100 px-1.5 py-0.5 rounded" data-id="element-1569">
                          RESET
                        </span>{' '}
                        to confirm
                      </Label>
                      <Input value={resetConfirmText} onChange={e => setResetConfirmText(e.target.value)} placeholder="Type RESET here" className="max-w-xs border-red-300 focus:border-red-500 focus:ring-red-500" autoFocus data-id="element-1570" />
                      <div className="mt-3 flex items-center gap-3" data-id="element-1571">
                        <button disabled={resetConfirmText !== 'RESET'} onClick={() => {
                    onResetAll();
                    setShowResetConfirm(false);
                    setResetConfirmText('');
                  }} className={`px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all ${resetConfirmText === 'RESET' ? 'bg-red-600 hover:bg-red-700 cursor-pointer' : 'bg-red-300 cursor-not-allowed'}`} data-id="element-1572">
                          <span className="flex items-center gap-2" data-id="element-1573">
                            <Trash2 className="w-4 h-4" data-id="element-1574" />
                            Confirm Reset
                          </span>
                        </button>
                        <button onClick={() => {
                    setShowResetConfirm(false);
                    setResetConfirmText('');
                  }} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 transition-colors" data-id="element-1575">
                          Cancel
                        </button>
                      </div>
                    </div>}
                </div>
              </div>
            </>}

          {activeTab === 'projections' && <>
              <Card data-id="element-1576">
                <CardHeader title="Planning Assumptions" subtitle="Return rates, contributions, and time horizon used across all projections" data-id="element-1577" />
                <CardContent className="space-y-6" data-id="element-1578">
                  <div data-id="element-1579">
                    <div className="flex items-center gap-2 mb-4" data-id="element-1580">
                      <div className="bg-emerald-100 p-2 rounded-lg" data-id="element-1581">
                        <TrendingUp className="w-5 h-5 text-emerald-600" data-id="element-1582" />
                      </div>
                      <h4 className="text-base font-semibold text-slate-800" data-id="element-1583">
                        Contributions & Timeline
                      </h4>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl" data-id="element-1584">
                      <div data-id="element-1585">
                        <Label data-id="element-1586">
                          Monthly Contribution ({baseCurrency})
                          <InfoTooltip text="Amount invested each month during accumulation. Added to liquid holdings. Stops at retirement." data-id="element-1587" />
                        </Label>
                        <Input type="number" min="0" step="100" value={settings.monthlyContribution} onChange={e => updateSetting('monthlyContribution', Number(e.target.value))} data-id="element-1588" />
                      </div>
                      <div data-id="element-1589">
                        <Label data-id="element-1590">
                          Time Horizon (years)
                          <InfoTooltip text="Total simulation window. The projection shows accumulation + drawdown within this period." data-id="element-1591" />
                        </Label>
                        <div className="flex items-center gap-3" data-id="element-1592">
                          <input type="range" min="1" max="60" step="1" value={settings.timeHorizon} onChange={e => updateSetting('timeHorizon', Number(e.target.value))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600" data-id="element-1593" />
                          <span className="font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded text-sm w-14 text-center shrink-0" data-id="element-1594">
                            {settings.timeHorizon}y
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-6" data-id="element-1595">
                    <div className="flex items-center gap-2 mb-4" data-id="element-1596">
                      <div className="bg-rose-100 p-2 rounded-lg" data-id="element-1597">
                        <TrendingUp className="w-5 h-5 text-rose-600" data-id="element-1598" />
                      </div>
                      <div data-id="element-1599">
                        <h4 className="text-base font-semibold text-slate-800" data-id="element-1600">
                          Inflation Adjustment
                        </h4>
                        <p className="text-xs text-slate-500" data-id="element-1601">
                          Increase retirement withdrawals annually to maintain
                          purchasing power
                        </p>
                      </div>
                    </div>
                    <div className="space-y-3 max-w-2xl" data-id="element-1602">
                      <label className="flex items-center gap-3 cursor-pointer" data-id="element-1603">
                        <input type="checkbox" checked={settings.inflationEnabled ?? false} onChange={e => updateSetting('inflationEnabled', e.target.checked)} className="w-4 h-4 rounded text-rose-600 focus:ring-rose-500" data-id="element-1604" />
                        <span className="text-sm text-slate-700" data-id="element-1605">
                          Enable inflation-adjusted drawdowns
                        </span>
                      </label>
                      {(settings.inflationEnabled ?? false) && <div data-id="element-1606">
                          <Label data-id="element-1607">
                            Annual Inflation Rate (%)
                            <InfoTooltip text="Your monthly drawdown will increase by this percentage each year of retirement. E.g., at 3% inflation, a 10,000/mo drawdown becomes 10,300/mo in year 2, 10,609/mo in year 3, etc." data-id="element-1608" />
                          </Label>
                          <div className="flex items-center gap-3" data-id="element-1609">
                            <input type="range" min="0.5" max="10" step="0.5" value={settings.inflationRate ?? 3} onChange={e => updateSetting('inflationRate', Number(e.target.value))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-rose-500" data-id="element-1610" />
                            <span className="font-semibold text-rose-700 bg-rose-50 px-2 py-0.5 rounded text-sm w-14 text-center shrink-0" data-id="element-1611">
                              {settings.inflationRate ?? 3}%
                            </span>
                          </div>
                        </div>}
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-6" data-id="element-1612">
                    <div className="flex items-center gap-2 mb-4" data-id="element-1613">
                      <div className="bg-amber-100 p-2 rounded-lg" data-id="element-1614">
                        <Flame className="w-5 h-5 text-amber-600" data-id="element-1615" />
                      </div>
                      <h4 className="text-base font-semibold text-slate-800" data-id="element-1616">
                        FIRE Target
                      </h4>
                    </div>
                    <div className="max-w-xs" data-id="element-1617">
                      <Label data-id="element-1618">
                        Desired Monthly Income ({baseCurrency})
                        <InfoTooltip text="How much you want to withdraw each month in retirement." data-id="element-1619" />
                      </Label>
                      <Input type="number" min="0" step="100" value={settings.desiredDrawdown} onChange={e => updateSetting('desiredDrawdown', Number(e.target.value))} data-id="element-1620" />
                      {settings.desiredDrawdown > 0 && <p className="text-xs text-slate-400 mt-1.5" data-id="element-1621">
                          ={' '}
                          {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: baseCurrency,
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    }).format(settings.desiredDrawdown * 12)}
                          /year
                        </p>}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-id="element-1622">
                <CardHeader title="Retirement Strategy" subtitle="Configure how your FIRE calculations are performed" data-id="element-1623" />
                <CardContent className="space-y-6" data-id="element-1624">
                  <div data-id="element-1625">
                    <div className="flex items-center gap-2 mb-4" data-id="element-1626">
                      <div className="bg-amber-100 p-2 rounded-lg" data-id="element-1627">
                        <Flame className="w-5 h-5 text-amber-600" data-id="element-1628" />
                      </div>
                      <h4 className="text-base font-semibold text-slate-800" data-id="element-1629">
                        Withdrawal Strategy
                      </h4>
                    </div>

                    <div className="space-y-4 max-w-2xl" data-id="element-1630">
                      <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${settings.withdrawalStrategy === 'drawdown' ? 'border-amber-500 bg-amber-50/50' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-1631">
                        <input type="radio" name="withdrawalStrategy" value="drawdown" checked={settings.withdrawalStrategy === 'drawdown'} onChange={() => onUpdateSettings({
                      ...settings,
                      withdrawalStrategy: 'drawdown'
                    })} className="mt-1 w-4 h-4 text-amber-600 focus:ring-amber-500" data-id="element-1632" />
                        <div data-id="element-1633">
                          <p className="font-semibold text-slate-800" data-id="element-1634">
                            Principal Drawdown (e.g., 4% Rule)
                          </p>
                          <p className="text-sm text-slate-500 mt-1" data-id="element-1635">
                            You gradually spend down your portfolio over your
                            time horizon. Results in a smaller FIRE target, but
                            your portfolio will eventually deplete.
                          </p>
                        </div>
                      </label>

                      <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${settings.withdrawalStrategy === 'interest-only' ? 'border-amber-500 bg-amber-50/50' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-1636">
                        <input type="radio" name="withdrawalStrategy" value="interest-only" checked={settings.withdrawalStrategy === 'interest-only'} onChange={() => onUpdateSettings({
                      ...settings,
                      withdrawalStrategy: 'interest-only'
                    })} className="mt-1 w-4 h-4 text-amber-600 focus:ring-amber-500" data-id="element-1637" />
                        <div data-id="element-1638">
                          <p className="font-semibold text-slate-800" data-id="element-1639">
                            Interest Only
                          </p>
                          <p className="text-sm text-slate-500 mt-1" data-id="element-1640">
                            You never touch the principal and only spend the
                            returns. Results in a larger FIRE target, but your
                            portfolio lasts indefinitely.
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>}

          {activeTab === 'exchange' && <>
              <section data-id="element-1641">
                <ExchangeRateWidget baseCurrency={baseCurrency} data-id="element-1642" />
              </section>
            </>}
        </div>
      </div>
    </div>;
}